import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from "@angular/core";
import { FaultCodesService } from "@app/main/modules/maintenance/_components/faultCodes/fault-codes.service";
import { IDiagnosticRecord } from "@app/main/modules/maintenance/_components/faultCodes/_models/IDiagnosticRecord";
import { IDiagnosticTroubleCode } from "@app/main/modules/maintenance/_components/faultCodes/_models/IDiagnosticTroubleCode";
import { SensorDataService } from "@app/main/modules/maintenance/_components/sensorData/sensor-data.service";
import { IVehicleWithSensorDataValues } from "@app/main/modules/maintenance/_components/sensorData/_models/IVehicleWithSensorData";
import { IAggregateFilterEvent } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { ITimelineEntryWithData } from "@directives/_veri/v-timeline/v-timeline.component";
import { LatestSensorDataValues } from "@interfaces/ISensorDataValue";
import { IVehicle } from "@interfaces/IVehicle";
import { DateService, DateUnit } from "@services/date.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { ZoneStatus } from "@temperature/_enums/temperature/ZoneStatus.enum";
import { IMultiStateInput } from "@temperature/_models/multiStateInputs/IMultiStateInput";
import { IMultiStateInputRecord } from "@temperature/_models/multiStateInputs/IMultiStateInputRecord";
import { IReeferWithMappings } from "@temperature/_models/reefer/IReeferWithMappings";
import { ITemperatureProbeWithTemperatureData } from "@temperature/_models/temperature/ITemperatureProbe";
import { ITemperatureRecord } from "@temperature/_models/temperature/ITemperatureRecord";
import { ITemperatureSetpoint } from "@temperature/_models/temperature/ITemperatureSetpoint";
import { ITemperatureSetpointHistory } from "@temperature/_models/temperature/ITemperatureSetpointHistory";
import { IZoneWithProbes } from "@temperature/_models/temperature/ITemperatureZone";
import { IZoneTemperatureOutOfBoundsHistory } from "@temperature/_models/temperature/ITemperatureZoneOutOfBoundsHistory";
import { MultiStateInputService } from "@temperature/_services/multi-state-input.service";
import { ReeferService } from "@temperature/_services/reefer.service";
import { TemperatureOutOfBoundsHistoryService } from "@temperature/_services/temperature-out-of-bounds.service";
import { TemperatureProbeService } from "@temperature/_services/temperature-probe.service";
import { TemperatureSetpointService } from "@temperature/_services/temperature-setpoint.service";
import { ZoneService } from "@temperature/_services/zone.service";
import { lastValueFrom } from "rxjs";
import './zoned-temperature-detail.component.scss';

@Component({
    selector: 'zoned-temperature-detail',
    template: require('./zoned-temperature-detail.component.html')
})
export class ZonedTemperatureDetailComponent implements OnInit, OnChanges {

    @Input() vehicleId: number;


    @Output() lastUpdateChanged = new EventEmitter<Date>();

    // ---

    loading: boolean;

    start: Date;
    end: Date;
    selectedZone: IZoneWithProbes;

    timelineEntries: ITimelineEntryWithData<number>[] = [];

    reeferUnit: IReeferWithMappings;
    zones: IZoneWithProbes[];
    vehicle: IVehicle;

    sensorDataValues: LatestSensorDataValues[];

    temperatureSetpoints: ITemperatureSetpoint[];
    temperatureSetpointHistories: ITemperatureSetpointHistory[];
    temperatureProbes: ITemperatureProbeWithTemperatureData[];
    temperatureRecords: ITemperatureRecord[];
    allTemperatureRecords: ITemperatureRecord[];
    zoneTemperatureOutOfBoundsHistory: IZoneTemperatureOutOfBoundsHistory[];

    diagnosticErrors: IDiagnosticTroubleCode[];
    allDiagnosticRecords: IDiagnosticRecord[];
    diagnosticRecords: IDiagnosticRecord[];
    diagnosticTroubleCodes: IDiagnosticTroubleCode[];

    latestMultiStateInputRecords: IMultiStateInputRecord[];
    multiStateInputs: IMultiStateInput[];

    constructor(
        private readonly dateService: DateService,
        private readonly faultCodeService: FaultCodesService,
        private readonly multiStateInputService: MultiStateInputService,
        private readonly outOfBoundsService: TemperatureOutOfBoundsHistoryService,
        private readonly reeferService: ReeferService,
        private readonly sensorDataService: SensorDataService,
        private readonly temperatureProbeService: TemperatureProbeService,
        private readonly temperatureSetpointService: TemperatureSetpointService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly zoneService: ZoneService) {
    }

    ngOnInit(): void {
        this.getData(true);
    }


    ngOnChanges(): void {
        this.setDefaultDateRange();
    }

    showZoneData(id: number) {
        this.selectedZone = this.zones.find(zone => {
            return zone.Id === id;
        });
    }

    onFilterChanged(event: IAggregateFilterEvent<any>) {
        if (!event.trigger) {
            return;
        }

        if (event.trigger === 'date') {
            this.start = event.date[0];
            this.end = event.date[1];
        }
    }

    //--

    private getData(first = false) {
        if (first) {
            this.loading = true;
        }
        Promise.all([
            lastValueFrom(this.reeferService.getReeferData({ vehicleId: this.vehicleId })),
            this.getPropertiesForReeferData()])
            .then(([reeferUnits, _]) => {
                this.reeferUnit = reeferUnits[0];

                this.reeferService.santitiseReeferSerialNumber(this.reeferUnit);

                this.reeferService.setupSensorDataForReefer(this.reeferUnit, this.vehicle as IVehicleWithSensorDataValues, this.sensorDataValues);
                this.multiStateInputService.getModes(this.reeferUnit, this.latestMultiStateInputRecords);
                this.temperatureSetpointService.mapHistoriesToSetPoints(this.temperatureSetpoints, this.temperatureSetpointHistories);

                this.diagnosticErrors = this.reeferService.getActiveReeferErrors(this.diagnosticRecords, this.diagnosticTroubleCodes);
                this.reeferUnit.multiStateInputRecords = this.latestMultiStateInputRecords;
                this.mapPropertiesToZones();
                this.reeferUnit.lastUpdated = this.reeferService.getLastUpdated(this.reeferUnit.zones, []);
                this.outputLastUpdated();
            }).finally(() => {
                this.setupTimeline();
                this.loading = false;
            })
    }

    private outputLastUpdated() {
        if (!this.reeferUnit.lastUpdated) {
            return;
        }
        this.lastUpdateChanged.emit(this.reeferUnit.lastUpdated);
    }

    private setDefaultDateRange() {
        this.end = new Date();
        this.start = this.dateService.addOrSubtractTime(this.end, -24, DateUnit.Hour);
    }

    private getPropertiesForReeferData() {
        const promises: PromiseLike<any>[] = [
            this.getVehicleData(),
            this.getZones(),
            this.getDiagnosticData(),
            this.getTemperatureData(),
            this.getMultiStateInputData()
        ]
        return Promise.all(promises);
    }

    private getVehicleData() {
        const vehiclePromise = this.vehicleService.getVehicle(this.vehicleId, { includePeripheralMappings: true }) as Promise<IVehicle>;
        const sensorDataPromise = this.sensorDataService.getLatestSensorDataValues({ VehicleId: this.vehicleId });

        return Promise.all([vehiclePromise, sensorDataPromise]).then(([vehicle, sensorDataValues]) => {
            this.vehicle = vehicle;
            this.sensorDataValues = sensorDataValues;
        })
    }

    private getZones() {
        return lastValueFrom(this.zoneService.getZones({ VehicleId: this.vehicleId, Status: ZoneStatus.Active })).then(zones => {
            this.zones = zones.sort((a, b) => a.Position - b.Position);
        })
    }

    private getDiagnosticData() {
        const faultCodePromises: any[] = [
            this.faultCodeService.getVehicleDiagnosticRecords(this.vehicleId),
            this.faultCodeService.getActiveDiagnosticRecordsForVehicle(this.vehicleId),
            this.faultCodeService.getReeferDiagnosticTroubleCodes()
        ]

        return Promise.all(faultCodePromises).then(([allDiagnosticRecords, diagnosticRecords, troubleCodes]) => {
            this.allDiagnosticRecords = this.reeferService.filterReeferErrorCodes(allDiagnosticRecords);
            this.diagnosticRecords = this.reeferService.filterReeferErrorCodes(diagnosticRecords);
            this.diagnosticTroubleCodes = troubleCodes;
        })
    }

    private getTemperatureData() {
        const temperaturePromises: any[] = [
            this.vehicleService.getLatestVehicleTemperatureRecordsForVehicle(this.vehicleId),
            this.vehicleService.getVehicleTemperatureRecords(this.vehicleId, this.start, this.end),
            lastValueFrom(this.temperatureSetpointService.getTemperatureSetpoints({ VehicleId: this.vehicleId })),
            lastValueFrom(this.temperatureSetpointService.getTemperatureSetpointHistories({ VehicleId: this.vehicleId })),
            lastValueFrom(this.temperatureProbeService.getTemperatureProbesForVehicle(this.vehicleId)),
            lastValueFrom(this.zoneService.getZonesTemperatureOutOfBoundsHistory({ VehicleId: this.vehicleId }))
        ];

        return Promise.all(temperaturePromises).then(([temperatureRecords, allTemperatureRecords, temperatureSetpoints, setpointHistories, temperatureProbes, outOfBoundsHistory]) => {
            this.temperatureRecords = temperatureRecords;
            this.allTemperatureRecords = allTemperatureRecords;
            this.temperatureSetpoints = temperatureSetpoints;
            this.temperatureSetpointHistories = setpointHistories;
            this.temperatureProbes = temperatureProbes;
            this.zoneTemperatureOutOfBoundsHistory = outOfBoundsHistory;
        })
    }

    private getMultiStateInputData() {
        const multiStateInputPromises: Promise<any>[] = [
            lastValueFrom(this.multiStateInputService.getLatestMultiStateInputRecords()),
            lastValueFrom(this.multiStateInputService.getMultiStateInputs({ VehicleId: this.vehicleId }))
        ];
        return Promise.all(multiStateInputPromises).then(([latestMultiStateInputRecords, multiStateInputs]) => {
            this.latestMultiStateInputRecords = latestMultiStateInputRecords.filter(record => record.VehicleId === this.vehicleId);
            this.multiStateInputs = multiStateInputs;
        })
    }

    private mapPropertiesToZones() {
        if (!this.zones || !this.zones.length) {
            this.reeferUnit.zones = [];
            return;
        }
        this.zones.forEach(zone => {
            if (!zone.Name) {
                zone.Name = this.translationService.t('ReeferList.ZoneNo', zone.Position);
            }
        })
        this.temperatureProbeService.mapTemperatureRecordsToProbes(this.temperatureProbes, this.allTemperatureRecords)

        const reeferZoneModes = this.zoneService.getLatestReeferZoneOperatingModes(this.latestMultiStateInputRecords, this.multiStateInputs, this.reeferService.getHardwareSource(this.reeferUnit.Make));
        this.zoneService.mapProbesToZone(this.zones, this.temperatureProbes);
        this.zoneService.mapZoneStatuses(this.zones, reeferZoneModes);
        this.zoneService.mapTemperatureSetpointsToZones(this.zones, this.temperatureSetpoints);
        this.temperatureProbeService.mapProbesLatestRecord(this.zones, this.temperatureRecords);
        this.zoneService.mapOutOfBoundsHistoryToZones(this.zones, this.zoneTemperatureOutOfBoundsHistory);
        this.zoneService.setZonesOperatingMode(this.zones);

        this.reeferUnit.zones = this.zones;
    }

    private setupTimeline(): void {

        this.timelineEntries = [];

        if (!this.vehicle || !this.zones?.length) {
            return;
        }

        const historyStartTime = this.dateService.addOrSubtractTime(new Date(), -1, DateUnit.Day);

        // Setpoint changes
        this.timelineEntries.push(...this.temperatureSetpointService.createTimelineEntriesForSetpointChanges(this.zones, historyStartTime));


        // Errors
        this.timelineEntries.push(...this.reeferService.createTimelineEntriesForDiagnosticErrors(this.allDiagnosticRecords, historyStartTime));


        // Out of bounds
        const recentOutOfBoundsHistory = this.zoneTemperatureOutOfBoundsHistory.filter(history => {
            return this.dateService.date1IsAfterDate2(history.StartTime, historyStartTime) || this.dateService.date1IsAfterDate2(history.EndTime, historyStartTime);
        })

        this.timelineEntries.push(...this.outOfBoundsService.createTimelineEntriesForOutOfBoundsHistory(recentOutOfBoundsHistory, this.zones, historyStartTime));

    }
}
